import { useState, type FormEvent } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { api } from "../../lib/api.js";
import { AuthCardShell } from "../../components/auth-card-shell.js";

const Search = z.object({ token: z.string().optional() });

export const Route = createFileRoute("/_public/reset-password")({
  validateSearch: Search,
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <AuthCardShell>
        <div className="brut-card">
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: "0 0 12px" }}>
            Invalid link
          </h1>
          <p>
            That URL is missing a reset token.{" "}
            <Link to="/forgot-password">Request a new one</Link>.
          </p>
        </div>
      </AuthCardShell>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post<{ ok: boolean }>("/auth/reset-password", {
        token,
        newPassword: password,
      });
      if (res.data.ok) {
        setDone(true);
        setTimeout(() => navigate({ to: "/login" }), 1200);
      } else {
        setError("That link is invalid or expired.");
      }
    } catch (err: unknown) {
      type ApiErr = { response?: { data?: { errors?: Array<{ message: string }>; message?: string } } };
      const data = (err as ApiErr).response?.data;
      setError(data?.errors?.[0]?.message ?? data?.message ?? "Reset failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <AuthCardShell>
        <div className="brut-card">
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: "0 0 12px" }}>
            Password updated
          </h1>
          <p>Redirecting to sign in…</p>
        </div>
      </AuthCardShell>
    );
  }

  return (
    <AuthCardShell>
      <div className="brut-card">
        <h1 style={{ fontSize: 32, fontWeight: 900, margin: "0 0 12px" }}>
          Set new password
        </h1>
        {error && <div className="brut-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ marginTop: 16 }}>
            <label className="brut-label" htmlFor="password">New password (min 8 chars)</label>
            <input
              id="password"
              type="password"
              className="brut-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
            />
          </div>
          <button
            type="submit"
            className="brut-button"
            disabled={submitting}
            style={{ marginTop: 24, width: "100%" }}
          >
            {submitting ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </AuthCardShell>
  );
}
